import { createAction, onRequestAddTimestamp } from '@/utils/api'
import { TradeStatus } from './TradeStatus'
import { FetchContext } from 'ofetch'
import {
  ITradeSubscribe,
  ITradeRedeem,
  ITradeFormRedeem,
  ITradeSwitch,
  IShareHolderInfo,
  IRedeemableInventory,
  IRelativeAgent,
  IRelativeSales,
} from './types'
import { ITradeProgress } from './TradeProgress'
import { IPagination } from '@/api/types'
import { Month } from '@/api/types/datetime'
import moment from 'moment-timezone';
import { useNow } from '@vueuse/core'

// use fake data
// import './mock'

// DOC: https://www.notion.so/UG-Website-API-2-2-1-Dealing-67ccdf2d339944909a904e1dd633f9a5?pvs=4

/**
 * 認購/贖回清單
 */
export const loadList = createAction<
  // Response
  {
    trades: ITradeSubscribe[] | ITradeRedeem[]
    pagination: IPagination
  },
  // Request
  {
    type: 'SBS' | 'RDM'
    from?: Month | '' | null
    to?: Month | '' | null
    account_id?: string | number | null
    status?: TradeStatus | null
    trust_code?: string | null
    page?: number | null
    perpage?: number | null
    /** 'delivered_at, desc' | 'delivered_at, asc' */
    order?: string | null
  }
>('GET', '/api/:locale/dealings/list/:type')

/**
 * 轉換清單
 */
export const loadListSwitch = createAction<
  // Response
  {
    trades: ITradeSwitch[]
    pagination: IPagination
  },
  // Request
  {
    from?: Month | '' | null
    to?: Month | '' | null
    account_id?: string | number | null
    trust_code?: string | null
    page?: number | null
    perpage?: number | null
    /** 'delivered_at, desc' | 'delivered_at, asc' */
    order?: string | null
  }
>('GET', '/api/:locale/dealings/list/switch')

/**
 * 認購/贖回/轉換 管理紀錄
 */
export const loadProgress = createAction<
  // Response
  {
    trades: ITradeProgress[]
  },
  // Request
  {
    type: 'SBS' | 'RDM' | 'SWITCH'
    from?: Month | null
    to?: Month | null
    holder_pid?: string | null
    status?: TradeStatus | null
  }
>('GET', '/api/:locale/dealings/manage/:type')

/**
 * 信託編號
 */
export const loadTrustCodes = createAction<
  // Response
  {
    trust_codes: string[]
  }
>('GET', '/api/:locale/dealings/trust_codes')

/**
 * 最新月份總覽 / 手續費加權平均 / 庫存表
 */
export const loadListNominee = createAction<
  // Response
  {
    fee_rate_months?: {
      from: Month
      to: Month
    } | null
    shareholders: IShareHolderInfo[]
    pagination: IPagination
  }
>('GET', '/api/:locale/dealings/nominee')

/**
 * 手續費加權平均 - 查詢 (Nominee only)
 */
export const loadFeeRate = createAction<
  // Response
  {
    fee: number
  }
>('GET', '/api/:locale/dealings/fee')

/**
 * 手續費加權平均 - 設定 (Nominee only)
 */
export const setFeeRate = createAction<
  // Response
  {},
  {
    fee: number
  }
>('POST', '/api/:locale/dealings/fee')

/**
 * 手續費加權平均 - 管理員設定
 */
export const setFeeRateAdmin = createAction<
  // Response
  {},
  {
    fee: number
    month: number
    agent_pid: string
  }
>('POST', '/api/:locale/dealings/fee_update')

/**
 * 下載手續費加權平均
 */
export const loadDownloadFeeRates = createAction<
  // Response
  Blob,
  {
    from?: Month | null
    to?: Month | null
    t: number
  }
>('GET', '/api/:locale/dealings/download/fee_rates', {
  onRequest: onRequestAddTimestamp
})

/**
 * 庫存表下載
 */
export const loadDownloadInventories = createAction<
  // Response
  {
    entries: {
      date: Month
      attachment_url: string
    }[]
  }
>('GET', '/api/:locale/dealings/download/inventories', {
  onRequest: onRequestAddTimestamp
})

/**
 * 取得可交易日期 (v2.2.2)
 */
export const loadTradeDates = createAction<
  // Response
  {
    dates: string[]
  }
>('GET', '/api/:locale/dealings/trade_dates', {
  onRequest: onRequestAddTimestamp
})

export interface ICalendarMonth {
  month: Month
  dealing_date: string
  sbs_closing_date?: string
  rdm_closing_date?: string
}

export const loadTradeCalendar = createAction<
  // Response
  {
    now: string
    calendar: ICalendarMonth[]
  }
>('GET', '/api/:locale/dealings/calendar', {
  onRequest: onRequestAddTimestamp
})

/** 
 * 取得交易日期 (必須還沒超過結單時間)
 * > 贖回與轉換只能選最近三個未截止的月份
 */
export function getTradeDates(type: 'SBS' | 'RDM' | 'SWITCH', refresh = false): Ref<string[]> {
  const now = useNow()
  const timeDiff = useState<number>('timeDiff', () => ref(0))
  const loadedCalendar = useState<ICalendarMonth[]>('loadedCalendar', () => ref([]))
  const currentMoment = computed(() => moment(now.value).add(timeDiff.value, 'ms'))
  if (loadedCalendar.value.length === 0 || refresh) {
    loadTradeCalendar().then(({ data }) => {
      loadedCalendar.value = [...data.value.calendar]
      timeDiff.value = data.value.now ? moment(data.value.now).diff(moment(now.value)) : 0
      const duration = moment.duration(timeDiff.value, 'ms')
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();
      const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      if (timeDiff.value > 100) console.warn(`電腦時間比交易系統${timeDiff.value > 0 ? '慢' : '快'}${formattedTime}`)
      console.info(`已較準為伺服器時間 ${currentMoment.value.format('YYYY-MM-DD HH:mm')}`)
    })
  }
  function isBeforeDateAt17(date: string): boolean {
    // 根據台北時區，17:00 為結單時間
    const dateAt17 = moment.tz(date, "Asia/Taipei").hour(17).minute(0).second(0);
    return currentMoment.value.isBefore(dateAt17);
  }
  if (type === 'SBS') {
    return computed(() => loadedCalendar.value.filter(c => c.sbs_closing_date && isBeforeDateAt17(c.sbs_closing_date)).map(c => c.dealing_date))
   } else {
    // 贖回與轉換只能選最近三個未截止的月份
    return computed(() => loadedCalendar.value.filter(c => c.rdm_closing_date && isBeforeDateAt17(c.rdm_closing_date)).map(c => c.dealing_date).splice(0, 3))
  }
}

/**
 * 取得可贖回庫存 (v2.2.2)
 */
export const loadRedeemableInventories = createAction<
  // Response
  {
    inventories: IRedeemableInventory[]
    draft_control: {
      fund_id: string
      draft_count: number
      delivered_at: null | string
    }
  },
  {
    agent_id?: number
    sales_id?: number
    account_id?: number
    redeem_date?: string
  }
>('GET', '/api/:locale/dealings/redeemable_inventories', {
  onRequest: onRequestAddTimestamp
})

/**
 * 計算費贖回費率 (v2.2.2)
 */
export const calcRedemptionCharge = createAction<
  // Response
  {
    redemption_charge: number
    message?: string
  },
  {
    trade_switch?: {
      switch_month: 'normal switch' | 'same month switch'
      switch_carry_fwd: 'carry fwd' | 'no carry fwd'
    },
    trade_redeem: {
      /** 贖回交易日 YYYY-MM-DD */
      date: string
      /** 贖回基金 */
      fund?: string
      /** 贖回方式 */
      method?: 'Shares' | 'Amount' | 'Inventory'
      /** 贖回金額 */
      amount?: number | null
      /** 贖回股數 */
      shares?: number | null
      /** 指定贖回 (Nominee Only) 不使用這支 API */
      // redeem_from: IFormRedeemInventory[]
    }
    account_id: number
  }
>('POST', '/api/:locale/dealings/calc_redemption_charge')

/**
 * 取得 Agents / Sub-sales (Deprecated, Migrated to v3)
 */

/**
 * 取得 Sub-sales (v2.2.2)
 */
export const loadSubSales = createAction<
  // Response
  {
    sub_sales: IRelativeSales[]
  }
>('GET', '/api/:locale/dealings/sub_sales')
